import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  FileText,
  FolderOpen,
  Forward,
  PenLine,
  RefreshCw,
} from 'lucide-react';
import { Modal, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX } from '../../../lib/zLayers';

export type AttachmentConflictChoice =
  | { action: 'overwrite' }
  | { action: 'rename'; name: string }
  | { action: 'skip' };

export interface SaveAttachmentConflictModalProps {
  isOpen: boolean;
  filename: string;
  folderName: string;
  suggestedName: string;
  onClose: () => void;
  onChoose: (choice: AttachmentConflictChoice) => void;
}

type ConflictAction = 'overwrite' | 'rename' | 'skip';

const SaveAttachmentConflictModal: React.FC<SaveAttachmentConflictModalProps> = ({
  isOpen,
  filename,
  folderName,
  suggestedName,
  onClose,
  onChoose,
}) => {
  const { colors } = useTheme();
  const [selectedAction, setSelectedAction] = useState<ConflictAction>('rename');
  const [renameValue, setRenameValue] = useState(suggestedName);
  const [renameError, setRenameError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedAction('rename');
    setRenameValue(suggestedName);
    setRenameError(null);
  }, [isOpen, filename, suggestedName]);

  if (!isOpen) return null;

  const confirm = () => {
    if (selectedAction === 'skip') {
      onChoose({ action: 'skip' });
      return;
    }
    if (selectedAction === 'overwrite') {
      onChoose({ action: 'overwrite' });
      return;
    }
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenameError('Enter a file name.');
      return;
    }
    if (trimmed === filename) {
      setRenameError('Choose a different name than the existing file.');
      return;
    }
    onChoose({ action: 'rename', name: trimmed });
  };

  const options: {
    id: ConflictAction;
    title: string;
    description: string;
    icon: React.ReactNode;
    accent: string;
    selectedRing: string;
    selectedBg: string;
  }[] = [
    {
      id: 'rename',
      title: 'Save with a different name',
      description: 'Keep the existing file and save this copy under a new name.',
      icon: <PenLine className="h-4 w-4" aria-hidden />,
      accent: 'text-indigo-600 dark:text-indigo-400',
      selectedRing: 'ring-indigo-500/40 border-indigo-300 dark:border-indigo-700',
      selectedBg: 'bg-indigo-50/90 dark:bg-indigo-950/30',
    },
    {
      id: 'overwrite',
      title: 'Replace existing file',
      description: 'The current file in the library will be permanently replaced.',
      icon: <RefreshCw className="h-4 w-4" aria-hidden />,
      accent: 'text-amber-600 dark:text-amber-400',
      selectedRing: 'ring-amber-500/40 border-amber-300 dark:border-amber-700',
      selectedBg: 'bg-amber-50/90 dark:bg-amber-950/25',
    },
    {
      id: 'skip',
      title: 'Skip this file',
      description: 'Do not save this attachment and continue with the rest.',
      icon: <Forward className="h-4 w-4" aria-hidden />,
      accent: 'text-slate-600 dark:text-slate-400',
      selectedRing: 'ring-slate-400/40 border-slate-300 dark:border-slate-600',
      selectedBg: 'bg-slate-50/90 dark:bg-slate-900/40',
    },
  ];

  const primaryLabel =
    selectedAction === 'overwrite'
      ? 'Replace file'
      : selectedAction === 'skip'
        ? 'Skip file'
        : 'Save with new name';

  const primaryClass =
    selectedAction === 'overwrite'
      ? 'bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-500 dark:bg-amber-600 dark:hover:bg-amber-500'
      : selectedAction === 'skip'
        ? ''
        : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 dark:bg-indigo-600 dark:hover:bg-indigo-500';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      zIndex={Z_INDEX.modalHigh + 10}
      closeOnEscape
    >
      <div
        className={cn(
          'flex shrink-0 items-start gap-4 border-b px-6 py-5',
          colors.border,
          'bg-gradient-to-r from-amber-50/90 via-white to-orange-50/50',
          'dark:from-amber-950/25 dark:via-gray-950 dark:to-orange-950/15',
        )}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white shadow-sm">
          <AlertTriangle className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className={cn('text-lg font-semibold', colors.text)}>Name already in use</h2>
          <p className={cn('mt-1 text-sm', colors.textMuted)}>
            This file name is already taken in the selected folder.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={cn('rounded-lg p-1.5', colors.textMuted, colors.interactiveHover)}
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
        <div
          className={cn(
            'rounded-xl border px-4 py-3.5',
            colors.border,
            'bg-gray-50/80 dark:bg-gray-900/35',
          )}
        >
          <p className={cn('text-[11px] font-bold uppercase tracking-wider mb-3', colors.textMuted)}>
            Conflict
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-gray-200 dark:bg-gray-950 dark:ring-gray-700">
                <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                  Incoming file
                </p>
                <p className={cn('text-sm font-semibold truncate', colors.text)} title={filename}>
                  {filename}
                </p>
              </div>
            </div>
            <ArrowRight
              className={cn('hidden sm:block h-4 w-4 shrink-0', colors.textMuted)}
              aria-hidden
            />
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-gray-200 dark:bg-gray-950 dark:ring-gray-700">
                <FolderOpen className="h-4 w-4 text-violet-600 dark:text-violet-400" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                  Destination
                </p>
                <p className={cn('text-sm font-semibold truncate', colors.text)} title={folderName}>
                  {folderName}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <p className={cn('text-sm font-semibold', colors.text)}>What should we do?</p>
          <div className="space-y-2" role="radiogroup" aria-label="Conflict resolution">
            {options.map((option) => {
              const selected = selectedAction === option.id;
              return (
                <div key={option.id} className="space-y-2">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      setSelectedAction(option.id);
                      setRenameError(null);
                    }}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-all',
                      colors.border,
                      selected
                        ? cn('ring-2 shadow-sm', option.selectedRing, option.selectedBg)
                        : 'bg-white hover:border-gray-300 dark:bg-gray-950/30 dark:hover:border-gray-600',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        selected
                          ? 'bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-950 dark:ring-white/10'
                          : 'bg-gray-100 dark:bg-gray-900/60',
                        option.accent,
                      )}
                    >
                      {option.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block text-sm font-semibold', colors.text)}>
                        {option.title}
                      </span>
                      <span className={cn('mt-0.5 block text-xs leading-relaxed', colors.textMuted)}>
                        {option.description}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'mt-1 h-4 w-4 shrink-0 rounded-full border-2 transition-colors',
                        selected
                          ? option.id === 'overwrite'
                            ? 'border-amber-600 bg-amber-600'
                            : option.id === 'rename'
                              ? 'border-indigo-600 bg-indigo-600'
                              : 'border-slate-500 bg-slate-500'
                          : 'border-gray-300 dark:border-gray-600',
                      )}
                      aria-hidden
                    >
                      {selected ? (
                        <span className="flex h-full w-full items-center justify-center">
                          <span className="h-1.5 w-1.5 rounded-full bg-white" />
                        </span>
                      ) : null}
                    </span>
                  </button>

                  {selected && option.id === 'rename' ? (
                    <div
                      className={cn(
                        'ml-11 mr-1 rounded-xl border px-4 py-3',
                        colors.border,
                        'bg-indigo-50/50 dark:bg-indigo-950/20',
                      )}
                    >
                      <label
                        className={cn('block text-xs font-semibold uppercase tracking-wide mb-2', colors.textMuted)}
                        htmlFor="conflict-rename"
                      >
                        New file name
                      </label>
                      <input
                        id="conflict-rename"
                        type="text"
                        value={renameValue}
                        onChange={(event) => {
                          setRenameValue(event.target.value);
                          setRenameError(null);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            confirm();
                          }
                        }}
                        className={cn(
                          'w-full rounded-lg border px-3 py-2 text-sm shadow-sm',
                          colors.border,
                          colors.text,
                          'bg-white dark:bg-gray-950',
                          'focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20',
                        )}
                        autoFocus
                      />
                      {renameError ? (
                        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{renameError}</p>
                      ) : (
                        <p className={cn('mt-2 text-xs', colors.textMuted)}>
                          Suggested name based on existing files in this folder.
                        </p>
                      )}
                    </div>
                  ) : null}

                  {selected && option.id === 'overwrite' ? (
                    <div
                      className={cn(
                        'ml-11 mr-1 flex gap-2.5 rounded-xl border px-4 py-3',
                        'border-amber-200 bg-amber-50/80 dark:border-amber-900/50 dark:bg-amber-950/25',
                      )}
                    >
                      <AlertTriangle
                        className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5"
                        aria-hidden
                      />
                      <p className="text-xs leading-relaxed text-amber-900 dark:text-amber-100">
                        The library copy of <span className="font-semibold">{filename}</span> will be
                        replaced. This cannot be undone.
                      </p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <ModalFooter className="!justify-between gap-4">
        <p className={cn('hidden sm:block text-xs', colors.textMuted)}>
          Your choice applies to this file only.
        </p>
        <div className="flex w-full shrink-0 flex-col-reverse gap-2 sm:w-auto sm:flex-row">
          <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Cancel save
          </Button>
          <Button
            type="button"
            variant={selectedAction === 'skip' ? 'secondary' : 'default'}
            onClick={confirm}
            className={cn('w-full sm:w-auto font-semibold', primaryClass)}
          >
            {primaryLabel}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SaveAttachmentConflictModal;
